import {FC, useEffect, useMemo, useRef, useState} from 'react'
import axios from 'axios'
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import {API_URL, GetBrowserData, brandName, toAbsoluteUrl} from '../../../../_metronic/helpers'
import {AppLoading} from '../../../../_metronic/helpers/components/AppLoading'
import {useAuth} from '../core/Auth'
import {AuthModel} from '../core/_models'
import {getUserByToken} from '../core/_requests'
import {getEntryPath} from '../core/postLoginPath'
import {
  TAX_FOLDER_SSO_LOGIN_URL,
  getTaxFolderSsoError,
  readTaxFolderSsoToken,
} from '../core/taxFolderSso'
import './SupportSsoCallback.css'

type TaxFolderSsoState =
  | {status: 'loading'; message: string}
  | {status: 'success'; message: string}
  | {status: 'error'; message: string}

// The portal token is single-use, so a re-run of the effect (React strict mode, a re-render)
// must reuse the exchange already in flight instead of spending the token twice.
const exchangeRequests = new Map<string, Promise<AuthModel>>()

const exchangeTaxFolderToken = (token: string): Promise<AuthModel> => {
  let request = exchangeRequests.get(token)
  if (!request) {
    request = GetBrowserData()
      .then((browser) => axios.post<AuthModel>(`${API_URL}/auth/sso`, {code: token, browser}))
      .then((response) => response.data)
    exchangeRequests.set(token, request)
    const forget = () => window.setTimeout(() => exchangeRequests.delete(token), 1000)
    request.then(forget, forget)
  }
  return request
}

const TaxFolderSsoCallback: FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const {saveAuth, setCurrentUser} = useAuth()
  const [state, setState] = useState<TaxFolderSsoState>({
    status: 'loading',
    message: 'در حال تأیید ورود از کارپوشه…',
  })
  const token = useMemo(() => readTaxFolderSsoToken(searchParams), [searchParams])
  // AuthProvider recreates saveAuth on every render; depending on it would restart the exchange
  // each time the session is stored and replay the single-use token.
  const authActionsRef = useRef({saveAuth, setCurrentUser})
  authActionsRef.current = {saveAuth, setCurrentUser}

  useEffect(() => {
    if (!token) {
      setState({
        status: 'error',
        message: 'اطلاعات ورود از کارپوشه دریافت نشد. لطفاً دوباره از کارپوشه وارد شوید.',
      })
      return
    }

    let active = true
    let sessionSaved = false
    let redirectTimer: number | undefined

    exchangeTaxFolderToken(token)
      .then(async (auth) => {
        if (!active) return
        if (!auth?.tk) {
          throw new Error('Invalid tax-folder SSO response')
        }
        authActionsRef.current.saveAuth(auth)
        sessionSaved = true
        const {data: user} = await getUserByToken(auth.tk)
        if (!active) return
        authActionsRef.current.setCurrentUser(user)
        setState({status: 'success', message: 'ورود انجام شد؛ در حال انتقال به پنل…'})
        redirectTimer = window.setTimeout(
          () => navigate(getEntryPath(user), {replace: true}),
          350
        )
      })
      .catch((error) => {
        if (!active) return
        if (sessionSaved) authActionsRef.current.saveAuth(undefined)
        setState({status: 'error', message: getTaxFolderSsoError(error, brandName)})
      })

    return () => {
      active = false
      if (redirectTimer) window.clearTimeout(redirectTimer)
    }
  }, [navigate, token])

  if (state.status === 'loading' || state.status === 'success') {
    return (
      <AppLoading
        mode='fullscreen'
        message={state.message}
        detail={
          state.status === 'success'
            ? 'حساب کاربری و کسب‌وکار شما در حال آماده‌سازی است'
            : 'هویت شما از طریق کارپوشه مالیاتی در حال بررسی است'
        }
      />
    )
  }

  return (
    <main className='support-sso-page'>
      <section className='support-sso-card' role='alert' aria-labelledby='taxFolderSsoTitle'>
        <img src={toAbsoluteUrl('/media/logos/default.svg')} alt={brandName} />
        <span className='support-sso-card__icon' aria-hidden='true'>
          <i className='las la-exclamation-triangle' />
        </span>
        <h1 id='taxFolderSsoTitle'>ورود با کارپوشه انجام نشد</h1>
        <p>{state.message}</p>
        <div className='support-sso-card__actions'>
          <a className='btn btn-primary' href={TAX_FOLDER_SSO_LOGIN_URL}>
            <i className='las la-redo-alt' aria-hidden='true' /> ورود دوباره با کارپوشه
          </a>
          <Link className='btn btn-light' to='/auth/login'>
            ورود با نام کاربری
          </Link>
        </div>
        <small>هر ورود از کارپوشه فقط یک بار و برای مدت کوتاهی معتبر است.</small>
      </section>
    </main>
  )
}

export {TaxFolderSsoCallback}
